import { useMemo, useRef, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Stack, Typography, Collapse, Tooltip, IconButton, useTheme } from '@mui/material'
import {
  ChevronDownIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  PlusIcon,
  Trash2Icon,
  ListTreeIcon,
} from 'lucide-react'

import { Button, Card, Skeleton, Switch } from '@repo/ui'
import { SelectField, EmptyState } from 'components'

import { useCanAccess, useFillBottom, useReferrerState } from 'hooks'
import { useSnackbar } from 'contexts'
import { ROUTES } from 'routes'
import { getIntegration } from 'constants/integrations'
import {
  useFieldMappingConfig,
  useFieldMappingCatalog,
  useSaveFieldMapping,
  useUnifiedFieldMappingConfig,
  useUnifiedFieldMappingCatalog,
  useSaveUnifiedFieldMapping,
} from 'hooks/api'
import type { FieldMappingObject } from 'services/fieldMappingApi'

interface Row {
  key: string
  object: FieldMappingObject
  vera_field: string
  source_field: string
  /** Which integration this row's source field comes from. Defaults to the page provider. */
  provider?: string
}

type SourceOpt = {
  label: string
  value: string
  group?: string
  disabled?: boolean
  disabledReason?: string
  rightAdornment?: React.ReactNode
}

const GROUPS: { object: FieldMappingObject; label: string }[] = [
  { object: 'product', label: 'Product' },
  { object: 'owner', label: 'Owner' },
  { object: 'tag', label: 'Tag' },
]

interface FieldMappingEditorProps {
  /** Single-provider mode: the provider whose connection is edited. */
  provider: string
  /** Full-page mode drops the "View In Field Mapping" link (already there). */
  fullPage?: boolean
  /** Human label for the provider, e.g. "Shopify". */
  providerLabel?: string
  /**
   * VRG-100 — unified cross-integration mode: aggregate every connected
   * integration's mappings, badge + group source fields by provider, and route
   * each save to the right connection. Used by the standalone Field-mapping page;
   * the per-integration tab stays single-provider (`unified={false}`).
   */
  unified?: boolean
}

export const FieldMappingEditor = ({
  provider,
  fullPage = false,
  providerLabel,
  unified = false,
}: FieldMappingEditorProps) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { open: notify } = useSnackbar()
  const { isHasAccess: canEdit } = useCanAccess({ action: 'field_mapping_edit' })
  // Embedded (!fullPage) this editor lives inside the integration page, so its
  // "View in field mapping" link leaves that section — declared above the early
  // returns below (rules of hooks).
  const referrer = useReferrerState('Integration')

  // Both hook sets are declared (rules-of-hooks); `enabled` fetches only the active one.
  const singleConfig = useFieldMappingConfig(provider, !unified)
  const singleCatalog = useFieldMappingCatalog(provider, !unified)
  const singleSave = useSaveFieldMapping(provider)
  const unifiedConfig = useUnifiedFieldMappingConfig(unified)
  const unifiedCatalog = useUnifiedFieldMappingCatalog(unified)
  const unifiedSave = useSaveUnifiedFieldMapping()

  const configQuery = unified ? unifiedConfig : singleConfig
  const catalogQuery = unified ? unifiedCatalog : singleCatalog

  // connection_id → provider, so unified rows can resolve their source integration.
  const providerByConnection = useMemo(() => {
    const map: Record<string, string> = {}
    if (unified && unifiedConfig.data) {
      for (const i of unifiedConfig.data.integrations) map[i.connection_id] = i.provider
    }
    return map
  }, [unified, unifiedConfig.data])

  // Deep-link target: the product page's "Use store default" links here with
  // ?highlight=sync to point at the global sync toggle this per-product mode inherits.
  const [searchParams] = useSearchParams()
  const syncToggleRef = useRef<HTMLDivElement>(null)
  const [highlightSync, setHighlightSync] = useState(false)

  useEffect(() => {
    if (searchParams.get('highlight') !== 'sync' || !configQuery.data) return
    const t = setTimeout(() => {
      syncToggleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightSync(true)
      setTimeout(() => setHighlightSync(false), 2200)
    }, 150)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, configQuery.data])

  /*
    Sizes the "No connected store" state below so its bottom sits the same 24px from the
    viewport bottom that the page keeps on its sides (§22). Declared HERE, with the other
    hooks, because the state it measures is behind an early return — rules of hooks.

    Enabled only in fullPage mode; the embedded (Integrations tab) copy keeps its fixed
    320px so the content under it stays on screen. The ref is attached only on the empty
    branch, which is fine: the hook's measure bails on a null ref and the observers it
    installs are inert until one exists.
  */
  const { ref: emptyFillRef, height: emptyFillHeight } = useFillBottom<HTMLDivElement>({
    enabled: fullPage,
  })

  const rowSeq = useRef(0)
  const nextKey = () => `row-${rowSeq.current++}`

  const [rows, setRows] = useState<Row[]>([])
  // VRG-103 — the visible "Sync value updates" switch below binds to THIS, not
  // `bidirectional`. `bidirectional` is a different/inert flag (Vera→source
  // writeback); it is still round-tripped on save so this editor never clobbers it,
  // but nothing here reads or displays it.
  const [bidirectional, setBidirectional] = useState(false)
  const [syncValueUpdatesEnabled, setSyncValueUpdatesEnabled] = useState(false)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  // Snapshot of the loaded server state, to detect dirtiness.
  const [snapshot, setSnapshot] = useState<string>('')

  const label = providerLabel || provider.charAt(0).toUpperCase() + provider.slice(1)

  // Hydrate local state once the config loads.
  useEffect(() => {
    if (!configQuery.data) return
    const loaded: Row[] = configQuery.data.mappings.map((m) => ({
      key: nextKey(),
      object: m.object,
      vera_field: m.vera_field,
      source_field: m.source_field,
      // In unified mode resolve the row's provider from its connection; otherwise
      // every row belongs to the page provider.
      provider: unified ? providerByConnection[m.connection_id] ?? provider : provider,
    }))
    setRows(loaded)
    setBidirectional(configQuery.data.bidirectional)
    setSyncValueUpdatesEnabled(configQuery.data.sync_value_updates_enabled)
    setSnapshot(serialize(loaded, configQuery.data.bidirectional, configQuery.data.sync_value_updates_enabled))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configQuery.data, unified, providerByConnection])

  const veraOptionsByObject = useMemo(() => {
    const map: Record<FieldMappingObject, { label: string; value: string }[]> = {
      product: [],
      owner: [],
      tag: [],
    }
    for (const f of catalogQuery.data?.veraFields ?? []) {
      map[f.object]?.push({ label: f.required ? `${f.label} *` : f.label, value: f.field })
    }
    return map
  }, [catalogQuery.data])

  const NOT_SUPPORTED_REASON =
    "This field isn't supported at import yet, so mapping it wouldn't take effect."

  // Single-provider source options (value = source key). The page catalog is one store.
  const sourceOptions = useMemo<SourceOpt[]>(
    () =>
      (catalogQuery.data?.sourceFields ?? []).map((s) => ({
        label: s.label,
        value: s.key,
        group: s.group,
        disabled: s.supported === false,
        disabledReason: s.supported === false ? NOT_SUPPORTED_REASON : undefined,
      })),
    [catalogQuery.data],
  )

  const integrationLabel = (p: string) =>
    getIntegration(p)?.name ?? p.charAt(0).toUpperCase() + p.slice(1)

  // VRG-100 — unified source options across ALL connected integrations. Grouped +
  // badged by integration (the picker the user chose); value is a composite
  // `provider:key` so the same source key across two stores stays distinct, and
  // selecting a field sets the row's provider (integration-grouped dropdown).
  const unifiedSourceOptions = useMemo<SourceOpt[]>(
    () =>
      (unified ? catalogQuery.data?.sourceFields ?? [] : []).map((s) => ({
        label: s.label,
        value: `${s.provider}:${s.key}`,
        group: integrationLabel(s.provider ?? ''),
        disabled: s.supported === false,
        disabledReason: s.supported === false ? NOT_SUPPORTED_REASON : undefined,
        rightAdornment: <SourceBadge provider={s.provider ?? ''} />,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unified, catalogQuery.data],
  )

  // Single-provider options for a row (badged with the page provider).
  const optionsForProvider = (rowProvider: string): SourceOpt[] => {
    const badge = <SourceBadge provider={rowProvider} />
    return sourceOptions.map((o) => ({ ...o, rightAdornment: badge }))
  }

  // Per-row source-select bindings — unified uses composite provider:key values.
  const sourceSelectOptions = (row: Row): SourceOpt[] =>
    unified ? unifiedSourceOptions : optionsForProvider(row.provider ?? provider)
  const sourceSelectValue = (row: Row): string =>
    unified ? (row.source_field ? `${row.provider}:${row.source_field}` : '') : row.source_field
  const onSourceChange = (row: Row, v: string) => {
    if (!unified) return updateRow(row.key, { source_field: v })
    const idx = v.indexOf(':')
    if (idx === -1) return updateRow(row.key, { source_field: v })
    updateRow(row.key, { provider: v.slice(0, idx), source_field: v.slice(idx + 1) })
  }

  const saveMutation = unified ? unifiedSave : singleSave

  const dirty = serialize(rows, bidirectional, syncValueUpdatesEnabled) !== snapshot
  const hasIncompleteRow = rows.some(
    (r) => (!!r.vera_field) !== (!!r.source_field),
  )
  const canSave = canEdit && dirty && !hasIncompleteRow && !saveMutation.isPending

  const addRow = (object: FieldMappingObject) =>
    setRows((prev) => [...prev, { key: nextKey(), object, vera_field: '', source_field: '' }])

  const updateRow = (key: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const removeRow = (key: string) => setRows((prev) => prev.filter((r) => r.key !== key))

  const handleCancel = () => {
    if (!configQuery.data) return
    const reset: Row[] = configQuery.data.mappings.map((m) => ({
      key: nextKey(),
      object: m.object,
      vera_field: m.vera_field,
      source_field: m.source_field,
      provider: unified ? providerByConnection[m.connection_id] ?? provider : provider,
    }))
    setRows(reset)
    setBidirectional(configQuery.data.bidirectional)
    setSyncValueUpdatesEnabled(configQuery.data.sync_value_updates_enabled)
  }

  const handleSave = async () => {
    const complete = rows.filter((r) => r.vera_field && r.source_field)
    try {
      let savedBidirectional = bidirectional
      let savedSyncValueUpdatesEnabled = syncValueUpdatesEnabled
      if (unified) {
        // Route each row to its provider; the service fans the write out per connection.
        const mappings = complete
          .filter((r) => r.provider)
          .map((r) => ({
            provider: r.provider as string,
            object: r.object,
            vera_field: r.vera_field,
            source_field: r.source_field,
          }))
        const saved = await unifiedSave.mutateAsync({
          bidirectional,
          sync_value_updates_enabled: syncValueUpdatesEnabled,
          mappings,
        })
        savedBidirectional = saved.bidirectional
        savedSyncValueUpdatesEnabled = saved.sync_value_updates_enabled
      } else {
        const mappings = complete
          .filter((r) => (r.provider ?? provider) === provider)
          .map((r) => ({ object: r.object, vera_field: r.vera_field, source_field: r.source_field }))
        const saved = await singleSave.mutateAsync({
          bidirectional,
          sync_value_updates_enabled: syncValueUpdatesEnabled,
          mappings,
        })
        savedBidirectional = saved.bidirectional
        savedSyncValueUpdatesEnabled = saved.sync_value_updates_enabled
      }
      setSnapshot(serialize(complete, savedBidirectional, savedSyncValueUpdatesEnabled))
      notify({ type: 'success', title: 'Field mapping saved' })
    } catch (e: any) {
      // `subtitle` is not a NotificationProps key — it was silently dropped, so a
      // failed save showed "Could not save" with no reason at all. The second line
      // is `text`.
      notify({ type: 'error', title: 'Could not save', text: e?.message })
    }
  }

  // No connected store: single-provider config 404s; unified returns an empty
  // integrations list.
  //
  // On the standalone page this is the MAPPING TAB's empty state, not the page's — the
  // Defaults half beside it needs no integration and is what an un-integrated company
  // actually lands on (see FieldsPage). The copy still points at connecting one, because
  // that is what unblocks this half.
  const noStore =
    configQuery.isError ||
    (unified && !!unifiedConfig.data && unifiedConfig.data.integrations.length === 0)
  if (noStore) {
    return (
      <Box
        ref={emptyFillRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Fill the region and centre INSIDE it, rather than centring inside 320px and
          // leaving the rest of the column empty. `flex: 1` did nothing here: the settings
          // content column is an ordinary Grid item in flow with no definite height, so
          // there was no free space to claim and the state sat high with a large dead zone
          // below it. Measured (`useFillBottom`, §22) rather than nudged with a top margin,
          // so it stays correct as the header/back-link row changes height.
          //
          // `minHeight` (not `height`) and the measured value only in fullPage mode: the
          // editor is also embedded in the Integrations → <provider> tab, where filling to
          // the viewport bottom would push the content below it off screen.
          minHeight: fullPage ? emptyFillHeight ?? 320 : 320,
        }}
      >
        <EmptyState
          title="No connected store"
          text={
            unified
              ? 'Connect an integration to configure field mapping.'
              : `Connect your ${label} store to configure field mapping.`
          }
          icon={<ListTreeIcon strokeWidth={1} size={32} />}
        />
      </Box>
    )
  }

  if (configQuery.isLoading || catalogQuery.isLoading) {
    return <FieldMappingEditorSkeleton fullPage={fullPage} />
  }

  return (
    <>
      <Card sx={{ p: fullPage ? 5 : 4 }}>
      {/* Header row: title + actions */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={3}>
        <Stack spacing={1}>
          <Typography variant="body1">Data to integrate</Typography>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Choose which {unified ? 'integration' : label} fields populate each Vera field.
            Unmapped fields keep their default behavior.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center" flexShrink={0}>
          {!fullPage && (
            <Button
              variant="text"
              disableElevation
              startIcon={<ListTreeIcon strokeWidth={1} size={16} />}
              onClick={() =>
                navigate(`${ROUTES.SETTINGS_COMPANY_FIELDS}?provider=${provider}`, {
                  state: referrer,
                })
              }
              sx={{ mr: 1 }}
            >
              View in Fields
            </Button>
          )}
          <Button variant="secondary" disableElevation disabled={!dirty} onClick={handleCancel}>
            Cancel
          </Button>
          <PermissionTooltip show={!canEdit}>
            <Button
              variant="primary"
              disableElevation
              disabled={!canSave}
              loading={saveMutation.isPending}
              onClick={handleSave}
            >
              Save
            </Button>
          </PermissionTooltip>
        </Stack>
      </Stack>

      {/* Global sync setting — the page-level mode that governs every mapping below (and
          that per-product sync overrides). Given its own bordered strip directly under the
          header, with an inline helper, so it reads as a section-governing setting rather
          than a peer of the view-only "Collapse all" control. */}
      <Box
        ref={syncToggleRef}
        sx={{
          mt: 5,
          px: 3,
          py: 2.5,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
          // Deep-link pulse target (product page → "company's global sync setting").
          // outline (not box-shadow/padding) so the ring adds no layout shift.
          outlineWidth: '2px',
          outlineStyle: 'solid',
          outlineColor: highlightSync ? 'brand.600' : 'transparent',
          outlineOffset: '4px',
          transition: 'outline-color 0.35s ease',
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant="body2">
            Sync value updates from {unified ? 'your stores' : label}
          </Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            When on, later edits in {unified ? 'your connected stores' : label} keep updating the
            imported values in Vera. When off, values import once and stay fixed. You can override
            this per product.
          </Typography>
        </Stack>
        <Switch
          checked={syncValueUpdatesEnabled}
          onChange={() => canEdit && setSyncValueUpdatesEnabled((v) => !v)}
          disabled={!canEdit}
        />
      </Box>

      {/* Controls row: collapse-all only (the sync setting now lives in the strip above). */}
      <Stack direction="row" alignItems="center" sx={{ mt: 5 }}>
        <Button
          variant="tertiary"
          disableElevation
          startIcon={
            Object.values(collapsed).every(Boolean)
              ? <ChevronsUpDownIcon strokeWidth={1} size={14} />
              : <ChevronsDownUpIcon strokeWidth={1} size={14} />
          }
          onClick={() => {
            const anyOpen = GROUPS.some(g => !collapsed[g.object])
            setCollapsed(anyOpen
              ? Object.fromEntries(GROUPS.map(g => [g.object, true]))
              : Object.fromEntries(GROUPS.map(g => [g.object, false]))
            )
          }}
        >
          {GROUPS.some(g => !collapsed[g.object]) ? 'Collapse all' : 'Expand all'}
        </Button>
      </Stack>

      {/* Groups */}
      <Stack spacing={2} sx={{ mt: 3 }}>
        {GROUPS.map(({ object, label: groupLabel }) => {
          const groupRows = rows.filter((r) => r.object === object)
          const isCollapsed = collapsed[object]
          return (
            <Box
              key={object}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 3, py: 3.5, cursor: 'pointer' }}
                onClick={() => setCollapsed((c) => ({ ...c, [object]: !c[object] }))}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                      transition: 'transform 0.15s ease',
                      display: 'flex',
                    }}
                  >
                    <ChevronDownIcon strokeWidth={1} size={18} />
                  </Box>
                  <Typography variant="body2">{groupLabel}</Typography>
                  {groupRows.length > 0 && (
                    <Typography variant="body4" color="text.secondary">
                      {groupRows.length}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              <Collapse in={!isCollapsed}>
                <Stack spacing={2} sx={{ px: 3, pb: 3, pt: 1 }}>
                  {groupRows.map((row) => (
                    <Stack key={row.key} direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <SelectField
                          placeholder="Vera field"
                          options={veraOptionsByObject[object]}
                          value={row.vera_field}
                          onChange={(v) => updateRow(row.key, { vera_field: v as string })}
                          disabled={!canEdit}
                          search
                        />
                      </Box>
                      <Typography variant="body4" color="text.secondary">
                        →
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <SelectField
                          placeholder={unified ? 'Integration field' : `${label} field`}
                          options={sourceSelectOptions(row)}
                          value={sourceSelectValue(row)}
                          onChange={(v) => onSourceChange(row, v as string)}
                          disabled={!canEdit}
                          search
                          endAdornment={
                            row.provider ? (
                              <SourceBadge provider={row.provider} />
                            ) : unified ? null : (
                              <SourceBadge provider={provider} />
                            )
                          }
                        />
                      </Box>
                      <Tooltip title="Remove mapping">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => removeRow(row.key)}
                            disabled={!canEdit}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: 'error.main' },
                            }}
                          >
                            <Trash2Icon strokeWidth={1} size={18} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}

                  <Box sx={{ alignSelf: 'flex-start', mt: groupRows.length > 0 ? 3 : 0 }}>
                    <PermissionTooltip show={!canEdit}>
                      <Button
                        variant="tertiary"
                        disableElevation
                        startIcon={<PlusIcon strokeWidth={1} size={16} />}
                        onClick={() => addRow(object)}
                        disabled={!canEdit}
                      >
                        Add mapping
                      </Button>
                    </PermissionTooltip>
                  </Box>
                </Stack>
              </Collapse>
            </Box>
          )
        })}
      </Stack>
    </Card>
    </>
  )
}

/**
 * Right-aligned badge naming the integration a source field is pulled from.
 * Today the page is single-provider so every row shows the same integration;
 * when the editor becomes cross-integration this reads a per-row provider.
 */
const SourceBadge = ({ provider }: { provider: string }) => {
  const integration = getIntegration(provider)
  if (!integration) return null
  return (
    <Stack direction="row" alignItems="center" spacing={1.75}>
      <Box
        component="img"
        src={integration.logo}
        alt=""
        sx={{ width: 18, height: 18, borderRadius: 0.75, objectFit: 'contain', flexShrink: 0 }}
      />
      <Typography variant="body4" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {integration.name}
      </Typography>
    </Stack>
  )
}

/** Wraps a disabled control with a permission tooltip (§27 access-gating). */
const PermissionTooltip = ({ show, children }: { show: boolean; children: React.ReactElement }) =>
  show ? (
    <Tooltip title="You need the 'Manage field mapping' permission. Ask an admin to grant it.">
      <span>{children}</span>
    </Tooltip>
  ) : (
    children
  )

// Skeleton mirrors the loaded editor 1:1 (§19 footprint parity) — same Card padding,
// header row, the controls row (Collapse-all left + sync toggle right) at the same
// mt:7 offset, and Product/Owner/Tag group boxes with the same px:3/py:3.5 headers and
// px:3/pb:3/pt:1 content, so there's no layout shift when data arrives.
const SkeletonGroup = ({
  rows,
  labelWidth,
  count,
}: {
  rows: number
  labelWidth: number
  count: boolean
}) => (
  <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}>
    {/* group header — matches the loaded header's px:3 py:3.5 (chevron + label + count) */}
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, py: 3.5 }}>
      <Skeleton sx={{ width: 18, height: 18 }} />
      <Skeleton sx={{ width: labelWidth, height: 16 }} />
      {count && <Skeleton sx={{ width: 14, height: 14 }} />}
    </Stack>
    {/* group content — matches px:3 pb:3 pt:1; each row = vera select / arrow / source select / delete */}
    <Stack spacing={2} sx={{ px: 3, pb: 3, pt: 1 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Stack key={i} direction="row" alignItems="center" spacing={2}>
          <Skeleton sx={{ flex: 1, height: 40, borderRadius: 1.5 }} />
          <Skeleton sx={{ width: 16, height: 16 }} />
          <Skeleton sx={{ flex: 1, height: 40, borderRadius: 1.5 }} />
          <Skeleton sx={{ width: 28, height: 28, borderRadius: 1 }} />
        </Stack>
      ))}
      {/* "Add mapping" — same mt:3 after rows as the loaded editor */}
      <Box sx={{ mt: rows > 0 ? 3 : 0 }}>
        <Skeleton sx={{ width: 120, height: 32, borderRadius: 2 }} />
      </Box>
    </Stack>
  </Box>
)

const FieldMappingEditorSkeleton = ({ fullPage }: { fullPage: boolean }) => (
  <Card sx={{ p: fullPage ? 5 : 4 }}>
    {/* Header: title + description (left), Cancel/Save (right) — spacing={3} like the real header */}
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={3}>
      <Stack spacing={1} sx={{ flex: 1 }}>
        <Skeleton sx={{ width: 150, height: 20 }} />
        <Skeleton lines={2} sx={{ maxWidth: 520 }} />
      </Stack>
      <Stack direction="row" spacing={3} flexShrink={0}>
        <Skeleton sx={{ width: 72, height: 36, borderRadius: 2 }} />
        <Skeleton sx={{ width: 72, height: 36, borderRadius: 2 }} />
      </Stack>
    </Stack>

    {/* Global sync-setting strip (mt:5): bordered, label + 2-line helper (left) + toggle. */}
    <Box
      sx={{
        mt: 5,
        px: 3,
        py: 2.5,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
      }}
    >
      <Stack spacing={0.75} sx={{ flex: 1 }}>
        <Skeleton sx={{ width: 220, height: 16 }} />
        <Skeleton lines={2} sx={{ maxWidth: 460 }} />
      </Stack>
      <Skeleton sx={{ width: 40, height: 24, borderRadius: 12 }} />
    </Box>

    {/* Controls row: collapse-all only (mt:5). */}
    <Stack direction="row" alignItems="center" sx={{ mt: 5 }}>
      <Skeleton sx={{ width: 104, height: 28, borderRadius: 2 }} />
    </Stack>

    {/* Groups — mt:3, spacing 2. Row counts mirror a typical loaded config so the
        skeleton's height ≈ the loaded page's (footprint parity, §19): Product is the
        field-rich object (4 rows), Owner moderate (2), Tag usually starts empty. */}
    <Stack spacing={2} sx={{ mt: 3 }}>
      <SkeletonGroup rows={4} labelWidth={80} count />
      <SkeletonGroup rows={2} labelWidth={64} count />
      <SkeletonGroup rows={0} labelWidth={40} count={false} />
    </Stack>
  </Card>
)

/** Order-independent serialization for dirty-checking (includes provider for unified). */
function serialize(rows: Row[], bidirectional: boolean, syncValueUpdatesEnabled: boolean): string {
  const norm = rows
    .map((r) => `${r.object}:${r.provider ?? ''}:${r.vera_field}:${r.source_field}`)
    .sort()
    .join('|')
  return `${bidirectional ? 1 : 0}${syncValueUpdatesEnabled ? 1 : 0}#${norm}`
}
